import { useState } from "react";
import { Droplets, Plus, Minus, Save, X } from "lucide-react";

const HydrationTracker = ({ current = 0, handleSave, handleCancel }) => {
    const [water, setWater] = useState(250);
    const [isAnimating, setIsAnimating] = useState(false);

    const handleIncrement = () => {
        setWater(prev => Math.min(prev + 50, 2000));
        triggerAnimation();
    };

    const handleDecrement = () => {
        setWater(prev => Math.max(prev - 50, 0));
        triggerAnimation();
    };

    const triggerAnimation = () => {
        setIsAnimating(true);
        setTimeout(() => setIsAnimating(false), 200);
    };

    const getProgressPercentage = () => {
        const dailyGoal = 2000; // 2L daily goal
        return Math.min(((current + water) / dailyGoal) * 100, 100);
    };

    const getWaveHeight = () => {
        return Math.min((water / 500) * 100, 100);
    };

    return (
        <div className="fixed inset-0 bg-black-50 flex items-center justify-center p-4 z-50">
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-3xl shadow-2xl max-w-md w-full overflow-hidden transform transition-all duration-300 hover:scale-105">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-500 to-cyan-500 p-6 text-white relative overflow-hidden">
                    <div className="absolute inset-0 opacity-20">
                        <div className="absolute inset-0 bg-white transform rotate-45 translate-x-12 translate-y-12"></div>
                    </div>
                    <div className="relative flex items-center justify-center space-x-3">
                        <Droplets className="w-8 h-8 animate-pulse" />
                        <h2 className="text-2xl font-bold tracking-wide">Hydration Tracker</h2>
                    </div>
                </div>

                {/* Main Content */}
                <div className="p-8">
                    {/* Question */}
                    <p className="p-style text-gray-700 text-lg font-medium text-center mb-8">
                        How much water did you drink?
                    </p>

                    {/* Water Amount Display with Animation */}
                    <div className="relative mb-8">
                        <div className="bg-white rounded-2xl p-6 shadow-lg border border-blue-100 relative overflow-hidden">
                            {/* Animated Wave Background */}
                            <div 
                                className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-blue-200 to-cyan-200 transition-all duration-500 rounded-2xl"
                                style={{ height: `${getWaveHeight()}%` }}
                            >
                                <div className="absolute inset-0 opacity-50">
                                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent animate-pulse"></div>
                                </div>
                            </div>
                            
                            {/* Amount Display */}
                            <div className="relative z-10 text-center">
                                <div className={`text-4xl font-bold text-blue-600 transition-all duration-200 ${isAnimating ? 'scale-110' : ''}`}>
                                    {water}
                                    <span className="text-2xl font-normal ml-1">ml</span>
                                </div>
                                <div className="text-lg text-gray-500 mt-1">Current serving</div>
                            </div>
                        </div>

                        {/* Control Buttons */}
                        <div className="flex items-center justify-center space-x-6 mt-6">
                            <button
                                onClick={handleDecrement}
                                disabled={water <= 0}
                                className="w-14 h-14 cursor-pointer rounded-full bg-gradient-to-r from-red-400 to-pink-400 text-white flex items-center justify-center shadow-lg hover:shadow-xl transform transition-all duration-200 hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                            >
                                <Minus className="w-6 h-6" />
                            </button>

                            <div className="flex-1 h-2 bg-gray-200 rounded-full mx-4 overflow-hidden">
                                <div 
                                    className="h-full bg-gradient-to-r from-blue-400 to-cyan-400 rounded-full transition-all duration-300"
                                    style={{ width: `${(water / 500) * 100}%` }}
                                ></div>
                            </div>

                            <button
                                onClick={handleIncrement}
                                disabled={water >= 2000}
                                className="w-14 h-14 cursor-pointer rounded-full bg-gradient-to-r from-green-400 to-emerald-400 text-white flex items-center justify-center shadow-lg hover:shadow-xl transform transition-all duration-200 hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                            >
                                <Plus className="w-6 h-6" />
                            </button>
                        </div>

                        {/* Quick Amount Buttons */}
                        <div className="flex justify-center space-x-2 mt-4">
                            {[100, 250, 500].map((amount) => (
                                <button
                                    key={amount}
                                    onClick={() => {
                                        setWater(amount);
                                        triggerAnimation();
                                    }}
                                    className={`px-4 py-2 rounded-full text-lg font-medium transition-all duration-200 cursor-pointer ${
                                        water === amount
                                            ? 'bg-blue-500 text-white shadow-lg'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                                >
                                    <span className="text-lg">{amount}ml</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Current Progress */}
                    <div className="bg-white rounded-xl p-4 shadow-lg border border-gray-100 mb-8">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-lg text-gray-600 font-medium">Today's Progress</span>
                            {/* <span className="text-lg text-gray-500">{Math.round(getProgressPercentage())}%</span> */}
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                            <div 
                                className="h-full bg-gradient-to-r from-blue-400 to-cyan-400 rounded-full transition-all duration-500"
                                style={{ width: `${getProgressPercentage()}%` }}
                            ></div>
                        </div>
                        <div className="text-center mt-3">
                            <span className="text-lg font-semibold text-gray-700">
                                {current}ml
                            </span>
                            <span className="text-gray-500 mx-2">+</span>
                            <span className="text-lg font-semibold text-blue-600">
                                {water}ml
                            </span>
                            <span className="text-gray-500 mx-2">=</span>
                            <span className="text-lg font-bold text-green-600">
                                {current + water}ml
                            </span>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex space-x-4">
                        <button
                            onClick={() => handleSave(water)}
                            className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white py-4 px-6 rounded-xl font-semibold shadow-lg hover:shadow-xl transform transition-all duration-200 hover:scale-105 flex items-center justify-center space-x-2 cursor-pointer"
                        >
                            <Save className="w-5 h-5" />
                            <span className="text-lg">Save</span>
                        </button>

                        <button
                            onClick={handleCancel}
                            className="flex-1 bg-gradient-to-r from-gray-400 to-gray-500 text-white py-4 px-6 rounded-xl font-semibold shadow-lg hover:shadow-xl transform transition-all duration-200 hover:scale-105 flex items-center justify-center space-x-2 cursor-pointer"
                        >
                            <X className="w-5 h-5" />
                            <span className="text-lg">Cancel</span>
                        </button>
                    </div>
                </div>

                {/* Decorative Elements */}
                <div className="absolute top-20 left-4 w-16 h-16 bg-blue-200 rounded-full opacity-20 animate-pulse"></div>
                <div className="absolute bottom-20 right-4 w-12 h-12 bg-cyan-200 rounded-full opacity-20 animate-pulse delay-1000"></div>
            </div>
        </div>
    );
};

export default HydrationTracker;


// import { useState } from "react";
// import { Button } from "@mui/material";

// const HydrationTracker = ({current, handleSave, handleCancel}) => {

//     const [water, setWater] = useState(250);

//     return (
//         <div className="form-wrapper">
//             <div className="confirm-modal" style={{backgroundColor: 'var(--site-light-background-color)', borderRadius: '15px'}}>
//                 <h2 className="h2-style">Hydration Tracker</h2>
//                 <p style={{margin: '10% 0 5px'}}>How much have you drank?</p>
//                 <div className="flex-buttons" style={{margin: '0 20% 10%'}}>
//                     <Button variant="outlined" size="medium" className="add-subtract-button" onClick={() => setWater(water - 50)}>-</Button>
//                     <p style={{fontWeight: 'bold'}}>{water}ml</p>
//                     <Button className="add-subtract-button" variant="outlined" size="medium" onClick={() => setWater(water + 50)}>+</Button>
//                 </div>
//                 <p>Consumed so far: <b style={{fontWeight: 'bold'}}>{current}ml</b></p>
//             </div>

//             <div className='bottom-buttons'>
//                 <Button
//                     style={{margin: "15px 5px 5px", width: '100px'}}
//                     onClick={() => handleSave(water)}
//                     variant="contained"
//                 >
//                     Save
//                 </Button>

//                 <Button
//                     style={{margin: "15px 5px 5px", width: '100px'}}
//                     onClick={handleCancel}
//                     variant="contained"
//                 >
//                     Cancel
//                 </Button>
//             </div>
//         </div>
//     )
// };

// export default HydrationTracker;